import * as Tabs from '@radix-ui/react-tabs';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { HeaderConnectButton } from '@/components/Header/HeaderButtons';
import { SankeyModal } from '@/components/Modal/SankeyModal';
import { TradeDetailsModal } from '@/components/Modal/TradeDetailsModal';
import type { TradeDetails } from '@/components/Modal/TradeDetailsModal';
import type { TradeDetailsSelection } from '@/components/TradeHistoryTable/types';
import { getTradeDetailsRouteIdentity } from '@/components/TradeHistoryTable/utils';

import { solana, SUPPORTED_CHAIN_IDS_LIMIT_ORDERS } from '@/constants/chain';

import { CheckoutMode } from '@/hooks/useCheckoutMode';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useTradeHistoryRoute } from '@/hooks/useTradeHistoryRoute';
import { useWalletOpenLimitOrders } from '@/hooks/useWalletOpenLimitOrders';

import { recentTransactionSelector, useTransactionStore } from '@/store/transaction';

import { NotificationIndicatorIcon } from '@/ui/Icons/NotificationIndicatorIcon';

// utils
import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';
import { EthereumAddress, SolanaAddress } from '@/utils/models';
import { applyOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';
import { routes } from '@/utils/routes';

import { tabsStyles } from '../../styles/tabs';
import { fontStyles } from '../../styles/typography';
import { classNames } from '../../utils/classnames';
import { OpenOrders } from './OpenOrders';
import { OrderHistory } from './OrderHistory';
import { FiltersReducerState } from './OrderHistory/Filters/reducer';

// defaults
export const PAGE_SIZE = 12;
export const DIVIDER_CHAR = '.';

const getTradeCheckoutMode = ({
  destinationChainId,
  chainId,
  liquiditySource,
}: {
  destinationChainId: number | undefined;
  chainId: number;
  liquiditySource: string | undefined;
}): CheckoutMode => {
  if (destinationChainId && chainId !== destinationChainId) return 'cross-chain';
  if (liquiditySource === MATCHA_LIMIT_ORDER) return 'advanced';
  return 'market';
};

export const SUPPORTED_TABS = ['swaps', 'limit-orders', 'open-orders'] as const;

export type TradeHistoryProps = {
  /** The currently selected tab. */
  selectedTab: (typeof SUPPORTED_TABS)[number];
  /** Initial page number. */
  initialPage?: number;
  /** Initial filters. */
  initialFilters?: FiltersReducerState;
  /** Initial search term. */
  initialSearchTerm?: string;
  /** Initial open order chain ID for persistence in URL. null selects all networks. */
  initialOpenOrderChainId: number | null;
};

const containerClass = classNames(
  'bg-special-background-card',
  'rounded-[20px]',
  'desktop:max-w-[1240px]',
  'overflow-x-hidden',
  'mx-auto',
);

const tabsClasses = tabsStyles({
  variant: 'card',
});

export const History = ({
  selectedTab,
  initialPage,
  initialFilters,
  initialSearchTerm,
  initialOpenOrderChainId,
}: TradeHistoryProps) => {
  // state
  const { ethereumWallet, solanaWallet } = useMatchaWallets();
  const funders = {
    ethereum: ethereumWallet?.address as EthereumAddress | undefined,
    solana: solanaWallet?.address as SolanaAddress | undefined,
  };
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);
  const [currentTab, setCurrentTab] = useState(selectedTab);
  const [selectedTrade, setSelectedTradeDetails] = useState<TradeDetails | undefined>();
  const [isSankeyModalOpen, setIsSankeyModalOpen] = useState(false);
  const [openOrderChainId, setOpenOrderChainId] = useState<number | null>(initialOpenOrderChainId);

  const { setRecentTransaction } = useTransactionStore(recentTransactionSelector);

  // wagmi is causing a hydration error in dev mode (and in the future potentially prod mode)
  // as the account address is persisted on the frontend and available on first render, thus
  // causing a mismatch between server and client side rendering.
  useEffect(() => {
    setIsMounted(true);
    setRecentTransaction(false);
    return () => setIsMounted(false);
  }, [setRecentTransaction]);

  // Sync currentTab from router.query.tab to handle browser back/forward navigation.
  // With shallow routing, getServerSideProps doesn't re-run so selectedTab prop stays stale.
  const queryTab = Array.isArray(router.query.tab) ? router.query.tab[0] : undefined;
  useEffect(() => {
    const tab =
      queryTab && SUPPORTED_TABS.includes(queryTab as (typeof SUPPORTED_TABS)[number])
        ? (queryTab as TradeHistoryProps['selectedTab'])
        : selectedTab;
    setCurrentTab(tab);
  }, [queryTab, selectedTab]);

  useEffect(() => {
    setOpenOrderChainId(initialOpenOrderChainId);
  }, [initialOpenOrderChainId]);

  // Sync openOrderChainId from URL when browser back/forward changes router.query.
  // With shallow routing, getServerSideProps doesn't re-run, so initialOpenOrderChainId
  // stays stale. This effect derives the chain ID directly from the query param.
  const queryNetworks =
    typeof router.query.networks === 'string' ? router.query.networks : undefined;
  useEffect(() => {
    if (!queryNetworks) {
      setOpenOrderChainId(null);
      return;
    }

    const supportedChainId = queryNetworks
      .split(DIVIDER_CHAR)
      .map(Number)
      .find(
        (chainId) => Number.isFinite(chainId) && SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(chainId),
      );

    if (supportedChainId !== undefined) {
      setOpenOrderChainId(supportedChainId);
    } else {
      setOpenOrderChainId(null);
    }
  }, [queryNetworks]);

  // handlers
  const handleTabClick = async (value: string) => {
    setCurrentTab(value as TradeHistoryProps['selectedTab']);

    const newQuery = { ...router.query, tab: value };

    await router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
  };

  const handleShowTradeDetails = (selectedTrade: TradeDetailsSelection) => {
    const routeIdentity = getTradeDetailsRouteIdentity(selectedTrade);
    if (!routeIdentity) return;
    router.push({
      pathname: routes.TX,
      query: {
        chainId: String(selectedTrade.chainId),
        txHash: routeIdentity.txHash,
        mode: getTradeCheckoutMode({
          destinationChainId: selectedTrade.destinationChainId,
          chainId: selectedTrade.chainId,
          liquiditySource: selectedTrade.liquiditySource,
        }),
        ...(routeIdentity.funderAddress ? { funderAddress: routeIdentity.funderAddress } : {}),
      },
    });
  };

  const handleCloseTradeDetails = () => setSelectedTradeDetails(undefined);

  const handleShowSankeyDiagram = () => setIsSankeyModalOpen(true);

  const handleOnPageChange = useCallback(
    (page: number) => {
      const query = { ...router.query };
      if (page === 1) {
        delete query.page;
      } else {
        query.page = page.toString();
      }
      router.push({ query }, undefined, { shallow: true });
    },
    [router],
  );

  const handleOnFiltersChange = (filters: FiltersReducerState) => {
    const { time, networks, orderTypes, status } = filters;

    const query = { ...router.query };
    delete query.page;

    if (time) {
      query.time = time.toString();
    } else {
      delete query.time;
    }
    if (networks.length) {
      query.networks = networks.join(DIVIDER_CHAR);
    } else {
      delete query.networks;
    }
    if (orderTypes.length) {
      query.orderTypes = orderTypes.join(DIVIDER_CHAR);
    } else {
      delete query.orderTypes;
    }
    if (status) {
      query.advancedOrderStatus = status;
    } else {
      delete query.advancedOrderStatus;
    }

    router.push({ query }, undefined, { shallow: true });
  };

  const handleOnSearchTermChange = useCallback(
    (searchTerm: string) => {
      const query = { ...router.query };
      if (searchTerm === '') {
        delete query.searchTerm;
      } else {
        query.searchTerm = searchTerm;
      }
      router.push({ query }, undefined, { shallow: true });
    },
    [router],
  );

  const handleOnFiltersReset = () => {
    router.push({ query: { tab: currentTab } }, undefined, { shallow: true });
  };

  const handleOnOpenOrderChainChange = useCallback(
    (newChainId: number | null) => {
      setOpenOrderChainId(newChainId);
      const query = { ...router.query };

      if (newChainId === null) {
        delete query.networks;
      } else {
        query.networks = newChainId.toString();
      }

      router.push({ query }, undefined, { shallow: true });
    },
    [router],
  );

  // fetch trade route
  const { data: routeData } = useTradeHistoryRoute({
    transactionHash: selectedTrade?.transactionHash,
    tradeId: selectedTrade?.tradeId,
    protocolVersion: selectedTrade?.protocolVersion,
  });

  // open orders notification
  const { data: openOrdersNotificationData } = useWalletOpenLimitOrders({
    funders,
    page: 1,
    limit: 1,
  });
  const hasOpenOrders = !!(
    openOrdersNotificationData && applyOpenLimitOrderTombstones(openOrdersNotificationData).total
  );

  return (
    <div className="bg-special-background-app bg-(image:var(--gradient-special-homepage))">
      {/* Space to take up position absolute sticker header. */}
      <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />

      <h1
        className={fontStyles({
          variant: 'H2',
          class: ['block', 'tablet:hidden', 'text-650', 'font-Bold', 'px-4', 'pt-4'],
        })}
      >
        Trade History
      </h1>

      {isMounted && (
        <div className="py-4 tablet:py-0 px-4 mb-3 tablet:mb-13">
          <h1
            className={fontStyles({
              variant: 'H2',
              class: ['hidden', 'tablet:block', 'max-w-[1240px]', 'mx-auto', 'my-6', 'text-left'],
            })}
          >
            Trade History
          </h1>

          {!ethereumWallet && !solanaWallet ? (
            <div
              className={classNames(
                containerClass,
                'flex',
                'items-center',
                'justify-center',
                'w-full',
                'h-[75vh]',
                'bg-transparent',
              )}
            >
              <div className="h-fit">
                <span
                  className={fontStyles({
                    variant: 'LABEL_LG',
                    class: ['flex', 'justify-center', 'mb-2', 'text-center'],
                  })}
                >
                  Trade History will appear here.
                </span>

                <div
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: ['flex', 'justify-center', 'mt-2', 'mb-4', 'text-token-gray-700'],
                  })}
                >
                  Connect wallet to see your trade history.
                </div>

                <span className="flex justify-center">
                  <HeaderConnectButton source="trade_history_page" />
                </span>
              </div>
            </div>
          ) : (
            <Tabs.Root
              value={currentTab}
              orientation="vertical"
              onValueChange={handleTabClick}
              className={containerClass}
            >
              <div className="no-scrollbar px-7 py-4.5">
                <Tabs.List aria-label="view transactions" className={tabsClasses.container()}>
                  <Tabs.Trigger className={tabsClasses.tab()} value="swaps">
                    Swaps
                  </Tabs.Trigger>

                  <Tabs.Trigger className={tabsClasses.tab()} value="limit-orders">
                    Advanced
                  </Tabs.Trigger>
                  <div className="shrink-0 w-px h-9 bg-(image:--gradient-token-divider) " />
                  <Tabs.Trigger
                    className={tabsClasses.tab({ class: 'relative' })}
                    value="open-orders"
                  >
                    Open orders{' '}
                    {hasOpenOrders && (
                      <NotificationIndicatorIcon className="absolute top-1.5 -right-[.375rem]" />
                    )}
                  </Tabs.Trigger>
                </Tabs.List>
              </div>
              <Tabs.Content value="swaps">
                {ethereumWallet ? (
                  <OrderHistory
                    funders={{ ethereum: funders.ethereum, solana: undefined }}
                    orderType="swaps"
                    handleShowTradeDetails={handleShowTradeDetails}
                    initialPage={initialPage}
                    initialFilters={initialFilters}
                    initialSearchTerm={initialSearchTerm}
                    onPageChange={handleOnPageChange}
                    onFiltersChange={handleOnFiltersChange}
                    onSearchTermChange={handleOnSearchTermChange}
                    onFiltersReset={handleOnFiltersReset}
                  />
                ) : (
                  <p className="text-center py-26">No trade history items to show.</p>
                )}
              </Tabs.Content>
              <Tabs.Content value="limit-orders">
                <OrderHistory
                  funders={funders}
                  orderType="limit"
                  handleShowTradeDetails={handleShowTradeDetails}
                  initialPage={initialPage}
                  initialFilters={initialFilters}
                  initialSearchTerm={initialSearchTerm}
                  onPageChange={handleOnPageChange}
                  onFiltersChange={handleOnFiltersChange}
                  onSearchTermChange={handleOnSearchTermChange}
                  onFiltersReset={handleOnFiltersReset}
                />
              </Tabs.Content>
              <Tabs.Content value="open-orders">
                <OpenOrders
                  funders={funders}
                  openOrderChainId={openOrderChainId}
                  onOpenOrderChainChange={handleOnOpenOrderChainChange}
                  setSelectedTradeDetails={setSelectedTradeDetails}
                />
              </Tabs.Content>
            </Tabs.Root>
          )}

          {/* Solana market swaps are still explorer-only; limit orders appear in-app. */}
          {ethereumWallet && solanaWallet && currentTab === 'swaps' && (
            <div
              className={fontStyles({
                variant: 'CAPTION',
                class: ['text-token-gray-700', 'mx-auto', 'my-6', 'desktop:max-w-[1240px]'],
              })}
            >
              Your Solana trades are available on&nbsp;
              <a
                className="text-token-purple-900"
                href={`${solana.blockExplorers.default.url}/account/${solanaWallet.address}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {solana.blockExplorers.default.name}
              </a>
            </div>
          )}
        </div>
      )}
      {selectedTrade && (
        <TradeDetailsModal
          isOpen={!!selectedTrade}
          onClose={handleCloseTradeDetails}
          onOpenChange={handleCloseTradeDetails}
          trade={selectedTrade}
          route={routeData?.route}
          handleShowSankeyDiagram={handleShowSankeyDiagram}
        />
      )}
      {selectedTrade && routeData?.route && (
        <span className="m-4">
          <SankeyModal
            route={routeData.route}
            chainId={Number(routeData.chainId)}
            isOpen={isSankeyModalOpen}
            onClose={() => setIsSankeyModalOpen(false)}
          />
        </span>
      )}
    </div>
  );
};
export { SearchBar } from './SearchBar';
